/* Keeps Dawn's cart submission and selected variant as the source of truth. */
if (!customElements.get('fg-purchase')) {
  customElements.define('fg-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('product-form form');
      this.modal = this.querySelector('.engraving-modal');
      if (!this.form || !this.modal) return;
      this.initialized = true;
      this.events = new AbortController();
      this.lang = this.dataset.lang || 'de';
      this.de = this.lang.startsWith('de');
      this.mode = 'plain';
      this.pricedOptions = this.dataset.pricedOptions === 'true';
      this.maxLength = Number(this.dataset.maxLength) || 15;
      this.inlineInput = this.querySelector('[data-fg-text]');
      this.inputWrap = this.querySelector('[data-fg-input-wrap]');
      this.error = this.querySelector('[data-fg-error]');
      this.counter = this.querySelector('[data-fg-counter]');
      this.hiddenInput = this.querySelector('#engraving-hidden');
      this.dataEl = this.querySelector('#engraving-data');
      this.modalInput = this.modal.querySelector('#engraving-text');
      this.modalError = this.modal.querySelector('[data-fg-modal-error]');
      this.confirm = this.modal.querySelector('#engraving-confirm-btn');
      this.canvas = this.modal.querySelector('#engraving-preview');
      this.context = this.canvas.getContext('2d');
      if (this.pricedOptions) {
        this.canvas.style.maxWidth = '400px';
        this.canvas.style.margin = '0 auto';
        this.canvas.style.display = 'block';
      }
      this.zoomButton = this.modal.querySelector('#zoom-toggle');
      this.submit = this.form.querySelector('[type="submit"]');
      this.dialog = this.modal.querySelector('.engraving-modal__dialog');
      this.variantPreviews = this.readJSON(this.querySelector('[data-fg-variant-previews]')?.textContent, {});
      this.basePreview = this.dataEl.dataset.preview || '';
      this.baseConfig = this.readJSON(this.dataEl.dataset.config, {});
      this.currentVariantId = this.form.querySelector('[name="id"]')?.value;
      this.previewStatus = document.createElement('p');
      this.previewStatus.className = 'engraving-preview-status';
      this.previewStatus.setAttribute('role', 'status');
      this.previewStatus.hidden = true;
      this.canvas.parentElement.appendChild(this.previewStatus);
      this.modal.classList.add('fg-purchase-modal');
      this.modal.setAttribute('aria-labelledby', `fg-modal-title-${this.dataset.section}`);
      this.modal.querySelector('h3').id = `fg-modal-title-${this.dataset.section}`;
      document.body.appendChild(this.modal);
      this.on(this, 'change', (event) => {
        if (!event.target.matches('[data-fg-mode]')) return;
        this.mode = event.target.value;
        this.updateMode();
        if (this.mode === 'engraved') this.openModal();
      });
      // A selected radio must reopen the preview as well.
      this.on(this, 'click', (event) => {
        if (!event.target.matches('[data-fg-mode][value="engraved"]')) return;
        this.mode = 'engraved';
        this.updateMode();
        this.openModal();
      });
      this.on(this.inlineInput, 'input', () => this.syncText(this.inlineInput.value));
      this.on(this.modalInput, 'input', () => this.syncText(this.modalInput.value));
      this.on(this.form, 'submit', (event) => this.handleSubmit(event), { capture: true });
      this.on(this.confirm, 'click', () => this.confirmEngraving());
      this.on(this.modal, 'click', (event) => {
        if (event.target.closest('[data-close]')) this.closeModal();
      });
      this.on(this.modal, 'keydown', (event) => this.handleModalKey(event));
      this.on(this.zoomButton, 'click', () => { this.zoomed = !this.zoomed; this.updateZoom(); });
      this.on(this.form.querySelector('[name="id"]'), 'change', () => {
        const id = this.form.querySelector('[name="id"]').value;
        if (id !== this.currentVariantId) {
          this.currentVariantId = id;
          this.confirmedOnce = false;
          this.syncVariantMode();
          this.loadPreview();
        }
      });
      this.observer = new MutationObserver(() => this.updateButton());
      this.observer.observe(this.submit, { attributes:true, attributeFilter:['disabled'] });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          const source = data.html.getElementById(`FGFacts-${this.dataset.section}`);
          const destination = this.section.querySelector(`#FGFacts-${this.dataset.section}`);
          if (source && destination) destination.innerHTML = source.innerHTML;
          const stock = this.section.querySelector(`#Inventory-${this.dataset.section}`);
          stock?.classList.toggle('fg-purchase-stock--unavailable', !data.variant?.available);
          this.translateVariants();
          this.currentVariantId = String(data.variant?.id || this.currentVariantId);
          this.syncVariantMode();
          this.updateVariantVideo(this.currentVariantId);
          this.updateButton();
        });
      }
      this.syncVariantMode();
      this.updateMode();
      this.translateVariants();
      this.loadPreview();
      this.updateVariantVideo(this.currentVariantId);
      document.fonts?.load('16px Allura').then(() => this.draw()).catch(() => {});
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.unsubscribe?.();
      if (this.modal?.classList.contains('is-open')) this.closeModal();
      this.modal?.remove();
      this.initialized = false;
    }

    on(target, type, handler, options = {}) {
      target?.addEventListener(type, handler, { ...options, signal:this.events.signal });
    }

    readJSON(value, fallback) {
      try { return JSON.parse(value || 'null') || fallback; } catch { return fallback; }
    }

    sanitize(value) {
      return (value || '').replace(/[^A-Za-z0-9ÄÖÜäöüß ,.!?\-:]/g, '').slice(0, this.maxLength);
    }

    syncText(value) {
      const cleaned = this.sanitize(value);
      this.inlineInput.value = cleaned;
      this.modalInput.value = this.mode === 'engraved' ? cleaned : '';
      this.hiddenInput.value = this.mode === 'engraved' ? cleaned.trim() : '';
      this.hiddenInput.disabled = this.mode !== 'engraved' || !cleaned.trim();
      this.counter.textContent = `${cleaned.length} / ${this.maxLength}`;
      this.error.hidden = true;
      if (this.modalError) this.modalError.hidden = true;
      this.modalInput.removeAttribute('aria-invalid');
      this.confirmedOnce = false;
      this.draw();
    }

    syncVariantMode() {
      if (!this.pricedOptions) return;
      this.mode = this.variantPreviews[this.currentVariantId]?.engraved ? 'engraved' : 'plain';
      this.updateMode();
    }

    updateMode() {
      this.inputWrap.hidden = this.mode !== 'engraved';
      this.inlineInput.disabled = this.mode !== 'engraved';
      this.syncText(this.inlineInput.value);
      this.updateButton();
    }

    updateButton() {
      if (this.submit.disabled) return;
      const label = this.submit.querySelector('span');
      if (label) label.textContent = this.mode === 'engraved'
        ? (this.de ? 'Gravur prüfen' : 'Review engraving')
        : (this.de ? 'In den Warenkorb' : 'Add to cart');
    }

    translateVariants() {
      if (!this.de) return;
      this.section.querySelectorAll('variant-selects legend').forEach((legend) => {
        if (legend.textContent.trim() === 'Handle') legend.textContent = 'Griff';
      });
      const labels = { 'Rosewood (Brown)':'Palisander (Braun)', 'Pekkawood (Black/Blue)':'Pakkaholz (Schwarz/Blau)' };
      this.section.querySelectorAll('variant-selects label').forEach((label) => {
        label.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && labels[node.textContent.trim()]) {
            node.textContent = labels[node.textContent.trim()];
          }
        });
      });
    }

    updateVariantVideo(variantId) {
      document.querySelectorAll('[data-fg-video-variant]').forEach((section) => {
        section.hidden = section.dataset.fgVideoGrips
          ? !section.dataset.fgVideoGrips.split(',').includes(this.variantPreviews[variantId]?.grip)
          : section.dataset.fgVideoVariant !== String(variantId);
        const video = section.querySelector('video');
        if (section.hidden) video?.pause();
        else video?.play()?.catch(() => {});
      });
    }

    handleSubmit(event) {
      if (this.confirmedOnce) { this.confirmedOnce = false; return; }
      if (this.mode === 'plain') {
        this.modalInput.value = '';
        this.hiddenInput.value = '';
        this.hiddenInput.disabled = true;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      this.syncText(this.inlineInput.value);
      this.openModal();
    }

    openModal() {
      if (this.modal.classList.contains('is-open')) return;
      this.returnFocus = document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.zoomed = false;
      this.updateZoom();
      this.draw();
      this.dialog.focus();
    }

    closeModal() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousOverflow || '';
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    handleModalKey(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.closeModal(); return; }
      if (event.key !== 'Tab') return;
      const elements = Array.from(this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), [tabindex="0"]'))
        .filter((el) => el.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    }

    confirmEngraving() {
      this.syncText(this.modalInput.value);
      if (!this.hiddenInput.value) {
        if (this.modalError) this.modalError.hidden = false;
        this.modalInput.setAttribute('aria-invalid', 'true');
        this.modalInput.focus();
        return;
      }
      if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      this.closeModal();
      this.confirmedOnce = true;
      this.form.requestSubmit(this.submit);
    }

    loadPreview() {
      const variantData = this.variantPreviews[this.currentVariantId] || {};
      // Variant-specific art needs its own positioning; otherwise retain the existing product preview.
      const variantSpecific = Boolean(variantData.preview && variantData.config?.text);
      this.config = variantSpecific ? variantData.config : this.baseConfig;
      const preview = variantSpecific ? variantData.preview : this.basePreview;
      this.zoomed = false;
      this.imageLoaded = false;
      this.previewStatus.hidden = true;
      this.canvas.hidden = false;
      this.zoomButton.hidden = false;
      const note = this.modal.querySelector('[data-fg-preview-note]');
      if (note && this.dataset.hasVariants === 'true' && !variantSpecific) {
        note.textContent = this.de
          ? 'Vorschau der Gravurposition. Die dargestellte Griffvariante kann abweichen.'
          : 'Preview of the engraving position. The handle shown may differ from your selection.';
      }
      const img = new Image();
      this.image = img;
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (this.image !== img) return;
        this.imageLoaded = true;
        this.canvas.width = img.naturalWidth;
        this.canvas.height = img.naturalHeight;
        this.updateZoom();
        this.draw();
      };
      img.onerror = () => {
        if (this.image !== img) return;
        this.canvas.hidden = true;
        this.zoomButton.hidden = true;
        this.previewStatus.hidden = false;
        this.previewStatus.textContent = this.de
          ? 'Das Vorschaubild ist gerade nicht verfügbar. Bitte prüfe deinen Gravurtext vor dem Hinzufügen.'
          : 'The preview image is currently unavailable. Please check your engraving text before adding it.';
      };
      if (preview) img.src = preview; else img.onerror();
    }

    updateZoom() {
      const zoom = this.config?.zoom;
      const text = this.config?.text;
      if (!this.zoomed || !zoom) { this.canvas.style.transform = 'scale(1)'; return; }
      const scale = Number(zoom.scale) || 1;
      const x = Number(zoom.tx) || Number(text?.x) || .5;
      const y = Number(zoom.ty) || Number(text?.y) || .5;
      this.canvas.style.transform = `scale(${scale}) translate(${(.5 - x) * 100}%, ${(.5 - y) * 100}%)`;
    }

    draw() {
      if (!this.imageLoaded || !this.context) return;
      const ctx = this.context;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.drawImage(this.image, 0, 0);
      const value = this.sanitize(this.modalInput.value);
      const config = this.config?.text;
      if (!value || !config) return;
      let fontSize = this.canvas.width * config.size;
      ctx.font = `${fontSize}px Allura`;
      if (config.width) {
        const maxWidth = this.canvas.width * config.width;
        const actualWidth = ctx.measureText(value).width;
        if (actualWidth > maxWidth) fontSize *= maxWidth / actualWidth;
        ctx.font = `${fontSize}px Allura`;
      }
      ctx.fillStyle = config.color || '#2b2119';
      ctx.textAlign = config.align || 'right';
      ctx.textBaseline = 'top';
      ctx.shadowColor = 'rgba(0,0,0,.1)';
      ctx.shadowBlur = 2;
      ctx.save();
      ctx.translate(this.canvas.width * config.x, this.canvas.height * config.y);
      ctx.rotate((Number(config.rotation) || 0) * Math.PI / 180);
      ctx.fillText(value, 0, 0);
      ctx.restore();
    }
  });
}
